import React, { useEffect, useState } from "react";
import Section from "../../components/Section";
import { pageTitle } from "../../helpers/PageTitle";
import axiosInstance from "../../utils/axiosInstance";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { addPrescription, editPrescription, fetchAppointmentDetails, imageBaseUrl } from "../../constants/ApiConstants";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { linearTopGradient, primaryColor, tuftsBlue } from "../../constants/colors";
import toast from "react-hot-toast";
import FullPageLoader from "../../components/FullPageLoader";
import AppointmentTimeInfo from "../AppointmentSummary/comps/AppointmentTimeInfo";
import AppointmentOrderSummary from "../AppointmentSummary/comps/AppointmentOrderSummary";
import DoctorDetailHeader from "../../components/DoctorDetailHeader";
import SecondaryButton from "../../components/SecondaryButton";
import Rating from "../../components/Rating";
import CommonMobileHeader from "../../components/Header/CommonMobileHeader";
import PatientAppointmentRequestCard from "../../components/PatientAppointmentRequestCard";
import TextAreaField from "../../components/TextAreaField";
import MyModal from "../../components/MyModal";
import InputField from "../../components/InputField";

export default function AddPrescriptions() {
  pageTitle("Medical Prescriptions");
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const userData = useSelector((state) => state.appSlice.userData);
  const doctorData = useSelector((state) => state.appSlice.doctorData);
  const [appointmentData, setAppointmentData] = useState();
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const initialPrescriptionProps = { title: "", quantity: "", dosage: "", mealTime: "", notes: "" };
  const mealTimeTypes = [
    { label: "After Meal", value: 0 },
    { label: "Before Meal", value: 1 },
  ];

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [addData, setAddData] = useState(initialPrescriptionProps);
  const [editData, setEditData] = useState({});

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteMedIndex, setDeleteMedIndex] = useState("");

  const [medicines, setMedicines] = useState([]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [coupon, setCoupon] = useState(null);
  //   {"notes":"","addMedicine":[{"title":"Dolo","quantity":1,"dosage":"Twice a day","mealTime":0,"notes":""}]}
  useEffect(() => {
    fetchAppointmentDetail();
  }, []);

  const fetchAppointmentDetail = () => {
    setLoading(true);
    axiosInstance
      .post(fetchAppointmentDetails, {
        appointment_id: appointmentId,
      })
      .then((res) => {
        setAppointmentData(res.data.data);
        if (res?.data?.data?.prescription) {
          const pres = JSON.parse(res?.data?.data?.prescription.medicine);
          setMedicines(pres.addMedicines);
          setNotes(pres.notes);
          setIsEdit(true);
        }
        if (res.data.data.coupon_title != null) {
          setCoupon({ coupon: res.data.data?.coupon_title });
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const onAddMedicineClick = () => {
    setShowAddModal(true);
  };
  const onEditMedicineClick = (pres) => {
    setEditData(pres);
    setShowEditModal(true);
  };
  const onAddClose = () => {
    setShowAddModal(false);
    setAddData(initialPrescriptionProps);
  };
  const onEditClose = () => {
    setShowEditModal(false);
    setEditData({});
  };

  const onCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteMedIndex("");
  };

  const handleContinue = () => {
    if (medicines.length === 0) {
      toast.error("Please provide medicines");
      return;
    }
    setSubmitting(true);
    axiosInstance
      .post(addPrescription, {
        appointment_id: appointmentId,
        user_id: appointmentData?.user?.id,
        medicine: JSON.stringify({
          notes: notes,
          addMedicines: medicines,
        }),
      })
      .then((res) => {
        if (res.data?.status) {
          toast.success("Prescription added successfully.");
          fetchAppointmentDetail();
        } else {
          toast.error(res.data?.message);
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleUpdatePrescription = () => {
    console.log(medicines);
    if (medicines.length === 0) {
      toast.error("Please provide medicines");
      return;
    }
    setSubmitting(true);
    axiosInstance
      .post(editPrescription, {
        prescription_id: appointmentData.prescription.id,
        user_id: appointmentData?.user?.id,
        medicine: JSON.stringify({
          notes: notes,
          addMedicines: medicines,
        }),
      })
      .then((res) => {
        if (res.data?.status) {
          toast.success("Prescription updated successfully.");
          fetchAppointmentDetail();
        } else {
          toast.error(res.data?.message);
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleAddSubmit = () => {
    if (!addData.title || !addData.quantity || !addData.dosage || ![0, 1].includes(addData.mealTime)) {
      toast.error("Please fill in all the fields.");
      return;
    }
    setMedicines([...medicines, addData]);
    onAddClose();
  };
  const handleEditSubmit = (index) => {
    if (!editData.title || !editData.quantity || !editData.dosage || ![0, 1].includes(editData.mealTime)) {
      toast.error("Please fill in all the fields.");
      return;
    }
    setMedicines(medicines.map((med, i) => (i === index ? editData : med)));
    onEditClose();
  };

  const handleDeleteClick = (index) => {
    setDeleteMedIndex(index);
    setShowDeleteModal(true);
  };

  const handleDeleteSubmit = (index) => {
    setMedicines(medicines.filter((med, i) => i != deleteMedIndex));
    onCloseDeleteModal(false);
  };

  return (
    <Wrapper>
      <Section topMd={20} topLg={20} topXl={110} bottomMd={200} bottomLg={150} bottomXl={110}>
        {loading && !appointmentData && <FullPageLoader />}
        {!loading && appointmentData && (
          <div className="content-wrapper">
            <CommonMobileHeader canBack={true} title={"Medical Prescriptions"} />
            <p className=" text-grey2 mx-2 py-0">Create prescriptions for</p>
            <div className="row">
              {doctorData ? (
                <PatientAppointmentRequestCard
                  key={appointmentData.id}
                  name={appointmentData.user?.fullname}
                  image={appointmentData.user?.profile_image}
                  dateOfBirth={appointmentData.user?.dob}
                  appointmentDate={appointmentData.date}
                  appointmentTime={appointmentData.time}
                  gender={appointmentData.user?.gender}
                  showView={false}
                  showAppointmentTimeAndDate={false}
                />
              ) : (
                <>
                  <strong className="text-black fw-bold m-3">{appointmentData?.appointment_number}</strong>
                  <div className="col-md-6">
                    <DoctorDetailHeader image={appointmentData?.doctor?.image} name={appointmentData?.doctor?.name} designation={appointmentData?.doctor?.designation} degrees={appointmentData?.doctor?.degrees} />
                  </div>
                </>
              )}

              {medicines?.length > 0 && (
                <div className="py-2 px-2">
                  {medicines.map((med, i) => (
                    <div className="d-flex bg-grey2 justify-content-between align-items-center mb-2 px-3 py-2" key={i}>
                      <div className="d-flex flex-column">
                        <strong className="text-dark fw-medium">{med.title}</strong>
                        <strong className="text-grey2 fs-md fw-medium">{med.mealTime === 0 ? "After Meal" : "Before Meal"}</strong>
                        <p className="fs-sm text-grey2 p-0 m-0">{med.dosage}</p>
                        <span className="text-sprimary fs-sm ">{med.notes}</span>
                      </div>
                      <div className="d-flex gap-3 align-items-center">
                        <strong className="text-dark fw-bold">{med.quantity}</strong>
                        <button className="py-2 px-3 bg-warning-surface rounded" onClick={() => onEditMedicineClick(med)}>
                          Edit
                        </button>
                        <button className="py-2 px-3 bg-danger-surface rounded" onClick={() => handleDeleteClick(i)}>
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="col-md-6 g-2 px-3">
                <button type="button" className="bg-grey2 py-2 w-100 fw-medium text-sprimary mb-4" onClick={onAddMedicineClick}>
                  Add Medicine
                </button>
                <TextAreaField placeholder={"Enter Notes"} label={"Notes"} rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
                <p></p>
                {<SecondaryButton title={isEdit ? "Update" : "Continue"} onClick={isEdit ? handleUpdatePrescription : handleContinue} loading={submitting} />}
              </div>
            </div>
          </div>
        )}

        {/* Add Prescription Modal */}
        <MyModal isOpen={showAddModal} modalTitle={"Add Prescription"} onClose={onAddClose}>
          <InputField label={"Name"} placeholder={"Medicine Name"} value={addData.title} onChange={(e) => setAddData({ ...addData, title: e.target.value })} />
          <InputField label={"Quanity (Total Count)"} inputType={"number"} placeholder={"Total Units"} value={addData.quantity} onChange={(e) => setAddData({ ...addData, quantity: e.target.value })} />
          <InputField label={"Dosage Details (Timing, Unit)"} sublabel={"Timing Unit: (EG: Twice a day, 1 tablet)"} value={addData.dosage} onChange={(e) => setAddData({ ...addData, dosage: e.target.value })} />
          <div className="d-flex gap-3 my-4">
            {mealTimeTypes.map((mealType) => (
              <button className={`mealType ${mealType.value === addData.mealTime ? "active" : ""}`} onClick={(e) => setAddData({ ...addData, mealTime: mealType.value })}>
                {mealType.label}
              </button>
            ))}
          </div>
          <TextAreaField placeholder={"Extra Notes"} rows={4} value={addData.notes} onChange={(e) => setAddData({ ...addData, notes: e.target.value })} />
          <div className="mt-5"></div>
          <SecondaryButton title={"Submit"} onClick={handleAddSubmit} />
        </MyModal>

        {/* Edit Prescription Modal */}
        <MyModal isOpen={showEditModal} modalTitle={"Edit Prescription"} onClose={onEditClose}>
          <InputField label={"Name"} placeholder={"Medicine Name"} value={editData.title} onChange={(e) => setEditData({ ...editData, title: e.target.value })} />
          <InputField label={"Quanity (Total Count)"} inputType={"number"} placeholder={"Total Units"} value={editData.quantity} onChange={(e) => setEditData({ ...editData, quantity: e.target.quantity })} />
          <InputField label={"Dosage Details (Timing, Unit)"} sublabel={"Timing Unit: (EG: Twice a day, 1 tablet)"} value={editData.dosage} onChange={(e) => setEditData({ ...editData, dosage: e.target.value })} />
          <div className="d-flex gap-3 my-4">
            {mealTimeTypes.map((mealType) => (
              <button className={`mealType ${mealType.value === editData.mealTime ? "active" : ""}`} onClick={(e) => setEditData({ ...editData, mealTime: mealType.value })}>
                {mealType.label}
              </button>
            ))}
          </div>
          <TextAreaField placeholder={"Extra Notes"} rows={4} value={editData.notes} onChange={(e) => setEditData({ ...editData, notes: e.target.value })} />
          <div className="mt-5"></div>
          <SecondaryButton title={"Submit"} onClick={handleEditSubmit} />
        </MyModal>

        {/*  Delete Medicine Modal */}

        {/* Edit Prescription Modal */}
        <MyModal isOpen={showDeleteModal} modalTitle={"Delete Prescription?"} onClose={onCloseDeleteModal}>
          <div className="py-3"></div>
          <SecondaryButton title={"Confirm"} onClick={handleDeleteSubmit} />
        </MyModal>
      </Section>
    </Wrapper>
  );
}
const Wrapper = styled.div`
  .mealType {
    padding: 6px 16px;
    background-color: #f5f5f5;
    border-radius: 30px;
    font-size: 0.95rem;
    color: #000;
    font-weight: 500;
  }
  .mealType.active {
    background-color: ${tuftsBlue};
    color: #fff;
  }
`;
